/**
 * AdminCampaignCodes — Admin CRUD for promotional signup codes.
 *
 * Codes are consumed by /auth/register when a prospect signs up via
 * /signup?code=<CODE>. Each code can (a) override the auto-assigned
 * signup plan and (b) apply a % discount for N billing cycles.
 *
 * Typical uses:
 *   · Free 14-day Pro trial:   grant_plan=pro, discount=100, months=1
 *   · Launch promo:            grant_plan=null, discount=50, months=3
 *   · Enterprise lead magnet:  grant_plan=enterprise, discount=100, months=1
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Check, X, Tag } from "lucide-react";

export default function AdminCampaignCodes() {
    const [codes, setCodes] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                api.get("/admin/campaign-codes"),
                api.get("/plans"),
            ]);
            setCodes(c.data.codes || []);
            setPlans(p.data.plans || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function toggleActive(code, active) {
        try {
            await api.patch(`/admin/campaign-codes/${code}`, { active });
            toast.success(active ? "Code activated" : "Code paused");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function del(code) {
        if (!window.confirm(`Delete code '${code}'? Existing redemptions are preserved but no new ones will be accepted.`)) return;
        try {
            await api.delete(`/admin/campaign-codes/${code}`);
            toast.success("Code deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    function copySignupUrl(code) {
        const url = `${window.location.origin}/signup?code=${code}`;
        navigator.clipboard.writeText(url);
        toast.success("Signup URL copied to clipboard");
    }

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-campaign-codes-page">
            <header className="mb-4 pb-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                    <h1 className="font-display font-black text-3xl text-ikb leading-tight">Campaign codes</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Promo codes for /signup?code=CODE. Grant a specific plan and/or apply a discount for N cycles.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowNew(true)}
                    className="h-9 px-3 border border-ikb bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5"
                    data-testid="cc-new"
                >
                    <Plus className="w-3.5 h-3.5" /> New code
                </button>
            </header>

            {loading ? (
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : codes.length === 0 ? (
                <div className="border border-border bg-white p-8 text-center">
                    <Tag className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                    <div className="font-medium text-sm">No campaign codes yet</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Create your first code to run a promo campaign like "LAUNCH50" or "PROTRIAL14".
                    </div>
                </div>
            ) : (
                <div className="border border-border bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 border-b border-border">
                            <tr>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Code</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Grant plan</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Discount</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Cycles</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Redeemed</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Expires</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Status</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes.map((c) => (
                                <tr key={c.code} className="border-b border-border hover:bg-secondary/40" data-testid={`cc-row-${c.code}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-mono font-bold text-ikb">{c.code}</div>
                                        {c.label && c.label !== c.code && <div className="text-[10px] text-muted-foreground">{c.label}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-xs uppercase font-mono">{c.grant_plan || <span className="text-muted-foreground">—</span>}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{c.discount_pct}%</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{c.months}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {c.redemptions || 0}
                                        {c.max_redemptions ? <span className="text-muted-foreground">/{c.max_redemptions}</span> : ""}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{c.expires_at?.slice(0, 10) || <span className="text-muted-foreground">Never</span>}</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${c.active ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-neutral-400 text-neutral-500 bg-neutral-50"}`}>
                                            {c.active ? "active" : "paused"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button type="button" onClick={() => copySignupUrl(c.code)}
                                            className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 inline-flex items-center gap-1"
                                            title="Copy signup URL"
                                            data-testid={`cc-copy-${c.code}`}>
                                            <Copy className="w-3 h-3" /> URL
                                        </button>
                                        <button type="button" onClick={() => toggleActive(c.code, !c.active)}
                                            className={`ml-1.5 h-7 px-2 border text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${c.active ? "border-amber-500 text-amber-700 bg-white hover:bg-amber-50" : "border-emerald-500 text-emerald-700 bg-white hover:bg-emerald-50"}`}
                                            data-testid={`cc-toggle-${c.code}`}>
                                            {c.active ? <><X className="w-3 h-3" /> Pause</> : <><Check className="w-3 h-3" /> Activate</>}
                                        </button>
                                        <button type="button" onClick={() => del(c.code)}
                                            className="ml-1.5 h-7 px-2 border border-rose-500 text-rose-700 bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-rose-50 inline-flex items-center gap-1"
                                            data-testid={`cc-delete-${c.code}`}>
                                            <Trash2 className="w-3 h-3" /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showNew && <NewCodeDialog plans={plans} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function NewCodeDialog({ plans, onClose, onCreated }) {
    const [form, setForm] = useState({
        code: "", label: "", grant_plan: "", discount_pct: 0, months: 1, max_redemptions: "", expires_at: "",
    });
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            const payload = {
                code: form.code.toUpperCase().trim(),
                label: form.label || null,
                grant_plan: form.grant_plan || null,
                discount_pct: parseFloat(form.discount_pct) || 0,
                months: parseInt(form.months) || 1,
                max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
                expires_at: form.expires_at || null,
            };
            await api.post("/admin/campaign-codes", payload);
            toast.success(`Code '${payload.code}' created`);
            onCreated();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="cc-new-dialog">
            <div className="bg-white border border-border max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="font-display font-black text-lg mb-3">New campaign code</div>
                <div className="space-y-3">
                    <Field label="Code" required>
                        <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                            placeholder="LAUNCH50" className="h-9 w-full border border-border bg-white text-sm px-2 uppercase font-mono" data-testid="cc-form-code" />
                    </Field>
                    <Field label="Label (optional)">
                        <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="50% off launch promo" className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-label" />
                    </Field>
                    <Field label="Grant plan (optional)">
                        <select value={form.grant_plan} onChange={(e) => setForm({ ...form, grant_plan: e.target.value })}
                            className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-plan">
                            <option value="">Keep signup default</option>
                            {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Discount %">
                            <input type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                                className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-discount" />
                        </Field>
                        <Field label="Months (cycles)">
                            <input type="number" min="1" max="36" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })}
                                className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-months" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Max redemptions">
                            <input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                                placeholder="Unlimited" className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-max" />
                        </Field>
                        <Field label="Expires at">
                            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                                className="h-9 w-full border border-border bg-white text-sm px-2" data-testid="cc-form-expires" />
                        </Field>
                    </div>
                </div>
                <div className="flex items-center gap-2 justify-end mt-4">
                    <button type="button" onClick={onClose} disabled={saving}
                        className="h-9 px-3 border border-border bg-white text-xs font-mono uppercase tracking-widest hover:bg-neutral-50">
                        Cancel
                    </button>
                    <button type="button" onClick={save} disabled={saving || !form.code}
                        className="h-9 px-3 border border-ikb bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-ikb/90 disabled:opacity-60 inline-flex items-center gap-1.5"
                        data-testid="cc-form-save">
                        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Create code
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                {label} {required && <span className="text-rose-600">*</span>}
            </div>
            {children}
        </label>
    );
}
